import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "checkbox", "display"]
  static values = { minDate: String, maxDate: String, taskId: String, key: String }

  connect() {
    // Store the original value to detect if it changed
    if (this.hasInputTarget) {
      this.originalValue = this.inputTarget.value
    }

    // Only auto-show the picker on initial load, not on reloads
    if (!this.element.dataset.reloaded) {
      this.showPicker()
    } else {
      // If this is a reload, just update constraints and wait for user click
      this.updateConstraints()
      // Remove the reload flag so next manual click can show picker
      delete this.element.dataset.reloaded
    }
  }

  updateConstraints() {
    if (this.hasInputTarget) {
      if (this.hasMinDateValue && this.minDateValue) {
        this.inputTarget.setAttribute('min', this.minDateValue)
      } else {
        this.inputTarget.removeAttribute('min')
      }

      if (this.hasMaxDateValue && this.maxDateValue) {
        this.inputTarget.setAttribute('max', this.maxDateValue)
      } else {
        this.inputTarget.removeAttribute('max')
      }
    }

    // After updating constraints, trigger cross-field validation if in bulk form
    const hasBulkDateFields = document.querySelector('[data-bulk-start-date]') || document.querySelector('[data-bulk-due-date]')
    if (hasBulkDateFields) {
      this.updateCrossFieldConstraints()
    }
  }

  updateCrossFieldConstraints() {
    const startDateInput = document.querySelector('[data-bulk-start-date]')
    const dueDateInput = document.querySelector('[data-bulk-due-date]')

    if (!startDateInput || !dueDateInput) return

    // Get parent task constraints from the date-badge-picker wrapper elements
    const startDateWrapper = startDateInput.closest('[data-controller="date-badge-picker"]')
    const dueDateWrapper = dueDateInput.closest('[data-controller="date-badge-picker"]')

    const parentMinDate = startDateWrapper?.dataset.dateBadgePickerMinDateValue || ''
    const parentMaxDate = startDateWrapper?.dataset.dateBadgePickerMaxDateValue || ''

    const startDate = startDateInput.value
    const dueDate = dueDateInput.value

    // Start date constraints:
    // - Min: parent's start date (if exists)
    // - Max: min of (due date if selected, parent's due date if exists)
    startDateInput.min = parentMinDate
    if (dueDate && parentMaxDate) {
      startDateInput.max = dueDate < parentMaxDate ? dueDate : parentMaxDate
    } else if (dueDate) {
      startDateInput.max = dueDate
    } else if (parentMaxDate) {
      startDateInput.max = parentMaxDate
    } else {
      startDateInput.max = ''
    }

    // Due date constraints:
    // - Min: max of (start date if selected, parent's start date if exists)
    // - Max: parent's due date (if exists)
    dueDateInput.max = parentMaxDate
    if (startDate && parentMinDate) {
      dueDateInput.min = startDate > parentMinDate ? startDate : parentMinDate
    } else if (startDate) {
      dueDateInput.min = startDate
    } else if (parentMinDate) {
      dueDateInput.min = parentMinDate
    } else {
      dueDateInput.min = ''
    }
  }

  showPicker() {
    // Update the input's min/max constraints with fresh values
    this.updateConstraints()
    if (this.hasInputTarget) {
      // Small delay to ensure DOM is ready and constraints are updated
      setTimeout(() => {
        this.inputTarget.showPicker()
      }, 100)
    }
  }

  reloadAndShowPicker(event) {
    event.preventDefault()

    // Mark that this is a reload so connect() doesn't auto-show picker
    this.element.dataset.reloaded = 'true'

    // Reload the edit view to get fresh constraints from server
    if (this.hasTaskIdValue && this.hasKeyValue) {
      const url = `/tasks/inline/date_badges/${this.taskIdValue}/edit?key=${this.keyValue}`
      const frame = this.element.closest('turbo-frame')

      if (frame) {
        // Set the frame src to reload with fresh data
        frame.src = url

        // After frame loads, manually trigger the picker (this is within user gesture)
        frame.addEventListener('turbo:frame-load', () => {
          const input = frame.querySelector('[data-date-badge-picker-target="input"]')
          if (input && input.showPicker) {
            // Update constraints first
            const controller = this.application.getControllerForElementAndIdentifier(
              frame.querySelector('[data-controller~="date-badge-picker"]'),
              'date-badge-picker'
            )
            if (controller) {
              controller.updateConstraints()
            }
            // Small delay to ensure constraints are applied
            setTimeout(() => input.showPicker(), 50)
          }
        }, { once: true })

        frame.reload()
      }
    }
  }

  handleInput(event) {
    // Input event fires when user selects a date from the picker
    // Mark that a change happened so we don't submit on blur
    this.dateWasChanged = true
  }

  handleBlur(event) {
    // When the date picker closes (blur event), always submit
    // Use setTimeout to let the change event fire first if it's going to
    setTimeout(() => {
      // If no change event fired, manually submit to return to show view
      if (!this.dateWasChanged) {
        const form = event.target.closest('form')
        if (form) {
          form.requestSubmit()
        }
      }
      // Reset the flag for next time
      this.dateWasChanged = false
    }, 50)
  }

  dateChanged(event) {
    // When a date is manually selected from the calendar, uncheck the "due_at_end_of_month" checkbox
    if (this.hasCheckboxTarget && event.target.value) {
      this.checkboxTarget.checked = false
    }

    // Mark that change happened
    this.dateWasChanged = true

    // Update the display text if we have a display target
    if (this.hasDisplayTarget) {
      if (event.target.value) {
        const date = new Date(event.target.value + 'T00:00:00')
        const formatted = (date.getMonth() + 1).toString().padStart(2, '0') + '/' +
                         date.getDate().toString().padStart(2, '0') + '/' +
                         date.getFullYear()
        this.displayTarget.textContent = formatted
      } else {
        // Date was cleared - reset to placeholder text
        const isBulkDueDate = event.target.hasAttribute('data-bulk-due-date')
        const isBulkStartDate = event.target.hasAttribute('data-bulk-start-date')
        if (isBulkDueDate) {
          this.displayTarget.textContent = 'Select due date'
        } else if (isBulkStartDate) {
          this.displayTarget.textContent = 'Select start date'
        } else {
          this.displayTarget.textContent = 'Select date'
        }
      }
    }

    // Update cross-field constraints for bulk form
    this.updateCrossFieldConstraints()

    // The change event will trigger form#autoSubmit which will update the turbo frame
    // and return to the show view automatically (only if taskId is set)
    // For bulk forms without taskId, this won't auto-submit
  }

  preventCheckbox(event) {
    // Prevent checking the checkbox when it's disabled (constraint doesn't allow end of month)
    event.preventDefault()
    event.stopPropagation()
    return false
  }
}
